import { AlertTriangle, CreditCard, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

interface PaymentGracePeriodBannerProps {
  daysRemaining: number;
  gracePeriodEndsAt: string;
  companyName: string;
  onDismiss: () => void;
}

export default function PaymentGracePeriodBanner({
  daysRemaining,
  gracePeriodEndsAt,
  companyName: _companyName,
  onDismiss,
}: PaymentGracePeriodBannerProps) {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const isUrgent = daysRemaining <= 2;
  const endDate = new Date(gracePeriodEndsAt);
  const formattedDate = endDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <div
      className={`w-full px-4 py-3 flex items-center justify-between gap-3 text-sm ${
        isUrgent ? "bg-red-600 text-white" : "bg-amber-500 text-white"
      }`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <AlertTriangle className="h-5 w-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold">Payment failed</span>
          {" — "}
          <span>
            {isUrgent
              ? `Your access will be suspended ${daysRemaining <= 1 ? "tomorrow" : `in ${daysRemaining} days`} (${formattedDate}) unless payment is resolved.`
              : `Please update your payment method. You have ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining before access is suspended.`}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="outline"
          className={`${
            isUrgent
              ? "border-white text-white hover:bg-red-700"
              : "border-white text-white hover:bg-amber-600"
          }`}
          onClick={() => window.open("/company-settings", "_self")}
        >
          <CreditCard className="h-4 w-4 mr-1.5" />
          Update Payment
        </Button>
        <button
          onClick={() => {
            setDismissed(true);
            onDismiss();
          }}
          className="p-1 rounded hover:bg-white/20 transition-colors"
          aria-label="Dismiss banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
